import React, { useEffect, useState, useReducer, useMemo, useCallback } from 'react';
import { useUserContext } from '../../components/user';
import { useEntityContext } from '../../util/entity-provider/context';
import { usePostMessageContext } from '../../post-message/PostMessage';
import { useAppData } from '../app-data/AppData';
import { usePostMessageListener } from '../entity-provider/usePostMessageListener';
import { getPermittedMenuConfig } from '../getPermittedMenuConfig';
import { AppContext } from './context';
import { API } from '../../components/api';
import { Spinner } from '../../components/core';
import { useBreakpoints } from '../../hooks/useBreakpoints';

const reducer = (state, action) =>
  Object.keys(action).reduce(
    (acc, key) => {
      acc[key] = action[key];
      return acc;
    },
    { ...state }
  );

const AppContextProvider = ({ children }) => {
  const [currentFeature, setCurrentFeature] = useState();
  const [state, dispatch] = useReducer(reducer, {});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [headerConfig, setHeaderConfig] = useState({});
  const [appEntityToken, setAppEntityToken] = useState(API.getAppEntityToken());

  const { appRef, appConfig } = state;
  const { isXl } = useBreakpoints();
  const { menuConfig, headerConfig: initialHeaderConfig, appId } = useAppData();
  const userContext = useUserContext();
  const entityContext = useEntityContext();

  useState(() => {
    setIsSidebarOpen(!isXl ? isSidebarOpen : false);
  }, [!isXl]);

  const isEntityPickerRequired = useMemo(() => {
    return appRef && appConfig?.isEntitySpecific === true;
  }, [appConfig, appRef]);

  const appConfigListener = useCallback(message => {
    const { type, ...rest } = message;
    if (type === 'app-config') {
      dispatch({ appRef: message?.appConfig?.crn, ...rest });
    } else if (type === 'app-entities') {
      dispatch({ appEntities: message?.appEntities });
    }
  }, []);

  usePostMessageListener(appConfigListener);

  const { sendMessage } = usePostMessageContext();

  useEffect(() => {
    sendMessage({ type: 'req-app-config' });
  }, [sendMessage]);

  useEffect(() => {
    const listen = () => {
      API.addListener(_event => {
        setAppEntityToken(API.getAppEntityToken());
      });
    };
    API.addListener(listen);
    return () => {
      API.removeListener(listen);
    };
  }, []);

  useEffect(() => {
    setHeaderConfig(initialHeaderConfig);
  }, [initialHeaderConfig]);

  const setAppEntities = useCallback(value => {
    dispatch({ appEntities: value });
  }, []);

  const setHeaderText = useCallback(value => {
    dispatch({ headerText: value });
  }, []);

  const setHeaderControls = useCallback(value => {
    dispatch({ headerControls: value });
  }, []);

  const permittedMenuConfig = useMemo(() => {
    // Temporary workaround to support both menuConfig shapes until everyone has changed to the new format.
    return getPermittedMenuConfig({ menuConfig: menuConfig.sidebarOptions || menuConfig, userContext, entityContext });
  }, [menuConfig, userContext, entityContext]);

  const toggleSidebar = () => {
    setIsSidebarOpen(prevState => !prevState);
  };

  if (appId !== 'mt' && isEntityPickerRequired === undefined) {
    return null;
  }

  return (
    <AppContext.Provider
      value={{
        setAppEntities,
        isEntityPickerRequired,
        menuConfig: permittedMenuConfig,
        headerConfig,
        setHeaderConfig,
        currentFeature,
        setCurrentFeature,
        setHeaderText,
        setHeaderControls,
        isSidebarOpen,
        toggleSidebar,
        ...state,
      }}
    >
      <Spinner spin={!appEntityToken && isEntityPickerRequired}>{children}</Spinner>
    </AppContext.Provider>
  );
};

export default AppContextProvider;
